/*
 * Reminders dashboard — /reminders
 *
 * Top filters: assignee (defaults to logged-in team member), status, priority,
 * due date range. Bottom: table of tasks. "+ New reminder" opens the form in a
 * CustomModal.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  FaRegBell,
  FaPlus,
  FaCheckSquare,
  FaRegSquare,
  FaClock,
  FaUserCircle,
  FaLink,
  FaFilter,
  FaRedoAlt,
  FaTag,
  FaRegCalendarAlt,
  FaTimes,
  FaRegEdit,
  FaRegTrashAlt,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { FaEllipsisVertical, FaRegNoteSticky, FaRegClone, FaCircleInfo } from 'react-icons/fa6';
import { TbCircleLetterT, TbCircleLetterS } from 'react-icons/tb';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import ConfirmationPopup from '../../util_components/confirmation_popup';
import { getCookie } from '../../../util_functions';
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  formatRelativeDue,
  isPastDue,
  isDueSoon,
  isDueWithinHour
} from './constants';
import {
  list_tasks,
  list_team_members,
  list_labels,
  create_task,
  update_task,
  delete_task,
  team_member_by_uuid,
  linked_user_by_uuid,
  label_by_uuid,
  current_team_member
} from './api';
import ReminderForm from './ReminderForm';
import PriorityIcon from './PriorityIcon';
import SearchableSelect from './SearchableSelect';
import ReminderDetailsModal from './ReminderDetailsModal';
import './reminders.css';

function FilterLabel({ children }) {
  return (
    <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      {children}
    </div>
  );
}

// Convert a task row into the shape ReminderForm expects in its `initial` prop.
// (Same shape used by RemindersModal — duplicated here to avoid a circular dep.)
const taskToFormInitial = (task) => {
  if (!task) return undefined;
  const m = moment(task.due_at);
  return {
    uuid: task.uuid,
    description: task.description || '',
    due_date: m.isValid() ? m.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    due_time: m.isValid() ? m.format('HH:mm') : moment().add(1, 'hour').format('HH:mm'),
    has_time: !!task.has_time,
    priority: task.priority || 'Medium',
    assignee_uuids: Array.isArray(task.assignee_uuids) ? task.assignee_uuids : [],
    linked_user_uuid: task.linked_user_uuid || '',
    label_uuids: task.label_uuids || [],
    alert_one: task.alerts?.[0]?.offset_minutes ?? '',
    alert_two: task.alerts?.[1]?.offset_minutes ?? ''
  };
};

// Custom sidebar ranges for the react-date-range picker. Overrides the
// library defaults (which omit "Tomorrow" / "Next Week" / "Next Month").
// Built lazily so "today" is computed at the moment the picker opens.
const buildStaticRanges = () =>
  createStaticRanges([
    {
      label: 'Today',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Tomorrow',
      range: () => ({
        startDate: moment().add(1, 'day').startOf('day').toDate(),
        endDate: moment().add(1, 'day').endOf('day').toDate()
      })
    },
    {
      label: 'Yesterday',
      range: () => ({
        startDate: moment().subtract(1, 'day').startOf('day').toDate(),
        endDate: moment().subtract(1, 'day').endOf('day').toDate()
      })
    },
    {
      label: 'This Week',
      range: () => ({
        startDate: moment().startOf('week').toDate(),
        endDate: moment().endOf('week').toDate()
      })
    },
    {
      label: 'Next Week',
      range: () => ({
        startDate: moment().add(1, 'week').startOf('week').toDate(),
        endDate: moment().add(1, 'week').endOf('week').toDate()
      })
    },
    {
      label: 'Last Week',
      range: () => ({
        startDate: moment().subtract(1, 'week').startOf('week').toDate(),
        endDate: moment().subtract(1, 'week').endOf('week').toDate()
      })
    },
    {
      label: 'This Month',
      range: () => ({
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate()
      })
    },
    {
      label: 'Next Month',
      range: () => ({
        startDate: moment().add(1, 'month').startOf('month').toDate(),
        endDate: moment().add(1, 'month').endOf('month').toDate()
      })
    },
    {
      label: 'Last Month',
      range: () => ({
        startDate: moment().subtract(1, 'month').startOf('month').toDate(),
        endDate: moment().subtract(1, 'month').endOf('month').toDate()
      })
    }
  ]);

/**
 * Trigger button + popover wrapping react-date-range's DateRange calendar.
 * Returns YYYY-MM-DD strings via onChange({ from, to }).
 */
function DateRangeFilter({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Position the popover relative to the trigger and recompute whenever the
  // page scrolls / resizes so it stays anchored. Rendered via a portal so the
  // overflow-auto on .common-page-container can't clip it.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const isEmpty = !from && !to;
  // The DateRange component manages a stateful range object. When the user
  // hasn't picked anything yet we seed it with today's date so the calendar
  // opens to "now" rather than the epoch.
  const range = [
    {
      startDate: from ? new Date(from) : new Date(),
      endDate: to ? new Date(to) : new Date(),
      key: 'selection'
    }
  ];

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;
    onChange({
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD')
    });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ from: '', to: '' });
  };

  const label = isEmpty
    ? 'Any date'
    : `${moment(from).format('MMM D')} → ${moment(to).format('MMM D')}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="reminders-trigger-btn inline-flex items-center gap-2 text-sm"
      >
        <FaRegCalendarAlt className="text-gray-400" size={12} />
        <span className={isEmpty ? 'text-gray-400' : ''}>{label}</span>
        {!isEmpty ? (
          <FaTimes size={10} onClick={handleClear} className="text-gray-400 hover:text-gray-600" />
        ) : null}
      </button>
      {open
        ? createPortal(
            // Rendered into <body>, so .common-page-container's overflow-auto
            // can't clip it. `.reminders-scope` is duplicated here so the
            // scoped overrides in reminders.css still apply to the
            // react-date-range internals.
            <div
              ref={popoverRef}
              className="reminders-scope"
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 10000
              }}
            >
              <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                {/* DateRangePicker = the full layout: predefined-ranges sidebar
                    (Today, Yesterday, This Week, Last Week, This Month, Last Month)
                    + the input-range fields + the two-calendar selector. */}
                <DateRangePicker
                  ranges={range}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  rangeColors={['#ee731b']}
                  showDateDisplay={false}
                  months={2}
                  direction="horizontal"
                  /* Hide the "N days up to today / starting today" input fields
                 — the static-range sidebar already covers the common cases. */
                  inputRanges={[]}
                  staticRanges={buildStaticRanges()}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/**
 * Kebab-style row actions menu. The table card has `overflow:hidden`, so a
 * pure `position:absolute` popover would clip on the last few rows. We
 * portal the menu to the body and anchor it to the trigger's right edge.
 */
function RowActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      // Anchor menu to the trigger's right edge, just below it.
      setCoords({ top: r.bottom + 4, left: r.right - 180 });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inTrig = triggerRef.current && triggerRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inTrig && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-tooltip-id="my-tooltip"
        data-tooltip-content="More actions"
        aria-label="More actions"
        className="reminders-icon-btn"
      >
        <FaEllipsisVertical size={14} />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="reminders-scope"
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 10000,
                width: 180
              }}
            >
              <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                {items.map((it) => (
                  <button
                    key={it.label}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      it.onClick();
                    }}
                    className="reminders-neutral-btn flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    style={it.destructive ? { color: '#dc2626' } : undefined}
                  >
                    {it.icon ? <it.icon size={13} /> : null}
                    <span className="flex-1">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function Reminders() {
  const cms_email = getCookie('cms_email');
  const me = useMemo(() => current_team_member(cms_email), [cms_email]);
  // CMS-wide timezone from the nav bar dropdown. Drives the rich tooltip and
  // any other time rendering on this page.
  const ianaTimezone = useSelector((state) => state.home?.iana_timezone || moment.tz.guess());

  const [team, setTeam] = useState([]);
  const [labels, setLabels] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Lookup map for the description-tooltip render callback (which only
  // receives the anchor's `content` string — we pass the task uuid through
  // and resolve to the full task object here).
  const tasksByUuid = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      m[t.uuid] = t;
    });
    return m;
  }, [tasks]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // The form modal is reused for create / edit / clone. We track each mode's
  // seed separately — `editingTask` carries a uuid so submit becomes an
  // update, `cloneSeed` strips the uuid so submit becomes a create.
  const [editingTask, setEditingTask] = useState(null);
  const [cloneSeed, setCloneSeed] = useState(null);
  // Read-only "view details + notes" modal — separate from edit/clone.
  const [viewingTask, setViewingTask] = useState(null);
  // Task pending delete-confirmation (null when no confirm dialog is open).
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  // Default sort: by due date ascending — most pressing reminders at the top.
  const [sortKey, setSortKey] = useState('due_at');
  const [sortDir, setSortDir] = useState('asc');

  // Default due-date window: 6 days back to 1 day ahead — covers what was
  // recently missed plus what's coming up today/tomorrow.
  const DEFAULT_DUE_FROM = moment().subtract(6, 'days').format('YYYY-MM-DD');
  const DEFAULT_DUE_TO = moment().add(1, 'days').format('YYYY-MM-DD');

  const [filters, setFilters] = useState({
    assignee_uuid: me?.uuid || '',
    status: 'pending',
    priority: '',
    label_uuid: '',
    due_from: DEFAULT_DUE_FROM,
    due_to: DEFAULT_DUE_TO
  });
  // Client-side title contains-filter. Applied after the server response, so
  // it doesn't re-fetch on every keystroke.
  const [titleFilter, setTitleFilter] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    const payload = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) payload[k] = v;
    });
    list_tasks(payload)
      .then((rows) => setTasks(rows))
      .catch((err) => {
        // Log so failures are debuggable but don't leave the spinner stuck
        // or produce an unhandled promise rejection on the dashboard.
        // eslint-disable-next-line no-console
        console.error('reminders: list_tasks failed', err);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  // Priority order for sort comparisons.
  const PRIORITY_RANK = { Urgent: 0, High: 1, Medium: 2, Low: 3, None: 4 };

  const sortedTasks = useMemo(() => {
    const q = titleFilter.trim().toLowerCase();
    const base = q ? tasks.filter((t) => (t.title || '').toLowerCase().includes(q)) : tasks;
    if (!sortKey) return base;
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      let av;
      let bv;
      if (sortKey === 'priority') {
        av = PRIORITY_RANK[a.priority] ?? 99;
        bv = PRIORITY_RANK[b.priority] ?? 99;
      } else if (sortKey === 'due_at') {
        av = moment(a.due_at).valueOf();
        bv = moment(b.due_at).valueOf();
      }
      if (av === bv) return 0;
      return av < bv ? -factor : factor;
    });
  }, [tasks, sortKey, sortDir, titleFilter]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Renders a clickable header cell with the appropriate sort glyph.
  function SortHeader({ label, sortBy, className = '' }) {
    const active = sortKey === sortBy;
    const Icon = active ? (sortDir === 'asc' ? FaSortUp : FaSortDown) : FaSort;
    return (
      <button
        type="button"
        onClick={() => toggleSort(sortBy)}
        className={`reminders-neutral-btn inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider ${active ? 'text-gray-800' : 'text-gray-600'} ${className}`}
        style={{ background: 'transparent' }}
      >
        {label}
        <Icon size={10} className={active ? 'text-mytPrimary' : 'text-gray-400'} />
      </button>
    );
  }

  useEffect(() => {
    list_team_members().then(setTeam);
    list_labels().then(setLabels);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateFilter = (patch) => setFilters((p) => ({ ...p, ...patch }));

  const clearFilters = () =>
    setFilters({
      assignee_uuid: '',
      status: '',
      priority: '',
      label_uuid: '',
      due_from: DEFAULT_DUE_FROM,
      due_to: DEFAULT_DUE_TO
    });

  const handleSubmit = (payload) => {
    if (payload.uuid) {
      const { uuid, ...patch } = payload;
      return update_task(uuid, patch, me?.name).then(() => {
        setShowCreate(false);
        setEditingTask(null);
        refresh();
      });
    }
    return create_task(payload, me?.name).then(() => {
      setShowCreate(false);
      setCloneSeed(null);
      refresh();
    });
  };

  const handleToggleComplete = (task) =>
    update_task(
      task.uuid,
      { status: task.status === 'completed' ? 'pending' : 'completed' },
      me?.name
    ).then(() => refresh());

  const handleStartEdit = (task) => {
    setCloneSeed(null);
    setEditingTask(task);
    setShowCreate(true);
  };
  const handleClone = (task) => {
    setEditingTask(null);
    setCloneSeed({
      ...task,
      uuid: undefined,
      status: 'pending',
      notes: [],
      history: []
    });
    setShowCreate(true);
  };
  const handleCloseModal = () => {
    setShowCreate(false);
    setEditingTask(null);
    setCloneSeed(null);
  };

  // Stage a delete request; the actual delete fires when the user confirms in
  // the in-app ConfirmationPopup below (avoids native window.confirm per the
  // codebase guideline in .github/copilot-instructions.md).
  const handleDelete = (task) => setTaskPendingDelete(task);

  const confirmDelete = () => {
    const task = taskPendingDelete;
    if (!task) return;
    delete_task(task.uuid).then(() => {
      if (editingTask?.uuid === task.uuid) setEditingTask(null);
      if (cloneSeed?.uuid === task.uuid) setCloneSeed(null);
      if (viewingTask?.uuid === task.uuid) setViewingTask(null);
      setTaskPendingDelete(null);
      refresh();
    });
  };

  // Renders the rich tooltip body for a task: due date in the linked user's
  // timezone (when available), each alert's fire-at in the same timezone, and
  // the full multi-line description.
  const renderTaskTooltip = (taskUuid) => {
    const task = tasksByUuid[taskUuid];
    if (!task) return null;
    // Use the CMS-wide timezone from the nav-bar dropdown so the tooltip
    // matches the rest of the operator's view. The zone code (e.g. IST,
    // PST, GMT) is appended in brackets by formatInTz.
    const tz = ianaTimezone;
    const tzLabel = tz;
    // Always format with full time + timezone code so the tooltip reads the
    // same regardless of the has_time flag (the form always sends a real
    // ISO timestamp, defaulting to a sane time when the user didn't pick one).
    // Bracket is added outside moment's format string since moment uses
    // `[ ]` as its own literal-escape syntax.
    const formatInTz = (m) => {
      const base = m.tz(tz).format('ddd, MMM D, YYYY, h:mm A');
      const zone = m.tz(tz).format('z');
      return `${base} [ ${zone} ]`;
    };
    const dueAtStr = formatInTz(moment(task.due_at));

    const offsetLabel = (mins) => {
      if (mins === 0) return 'At time of event';
      if (mins < 60) return `${mins} min before`;
      if (mins < 1440) {
        const h = mins / 60;
        return `${h % 1 === 0 ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'} before`;
      }
      if (mins < 10080) {
        const d = mins / 1440;
        return `${d % 1 === 0 ? d : d.toFixed(1)} day${d === 1 ? '' : 's'} before`;
      }
      const w = mins / 10080;
      return `${w % 1 === 0 ? w : w.toFixed(1)} week${w === 1 ? '' : 's'} before`;
    };

    const alertFireAt = (mins) => formatInTz(moment(task.due_at).subtract(mins, 'minutes'));

    return (
      <div style={{ minWidth: 260 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#f8fafc' }}>
          {task.title}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11.5 }}>
          <div>
            <div
              style={{
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: 10
              }}
            >
              Due · {tzLabel}
            </div>
            <div style={{ color: '#f1f5f9' }}>{dueAtStr}</div>
          </div>

          {Array.isArray(task.alerts) && task.alerts.length > 0 ? (
            <div>
              <div
                style={{
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontSize: 10
                }}
              >
                Alerts · {tzLabel}
              </div>
              {task.alerts.map((a, i) => (
                <div key={`${a.offset_minutes}-${i}`} style={{ color: '#f1f5f9' }}>
                  {offsetLabel(a.offset_minutes)} — {alertFireAt(a.offset_minutes)}
                </div>
              ))}
            </div>
          ) : null}

          {task.description && task.description.trim() ? (
            <div>
              <div
                style={{
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontSize: 10
                }}
              >
                Description
              </div>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e2e8f0' }}>
                {task.description}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <CommonHeader title="Reminders" loading={loading}>
      <div className="reminders-scope">
        {/* Dedicated tooltip for task descriptions — pre-wrap preserves
            newlines from multi-line descriptions; the dark "card" look
            with soft shadow + rounded corners matches modern productivity
            tools (Linear / Notion). */}
        <ReactTooltip
          id="reminders-task-tooltip"
          place="top"
          delayShow={150}
          delayHide={60}
          render={({ content }) => renderTaskTooltip(content)}
          style={{
            background: '#0f172a',
            color: '#f8fafc',
            maxWidth: 420,
            padding: '12px 14px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.12)',
            fontSize: 12,
            lineHeight: 1.5,
            zIndex: 100
          }}
          opacity={1}
        />
        {/* Top bar */}
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-1.5 self-center pr-2 text-gray-600">
            <FaFilter size={11} />
            <span className="text-xs font-semibold uppercase tracking-wider">Filters</span>
          </div>
          <div>
            <FilterLabel>Due date</FilterLabel>
            <DateRangeFilter
              from={filters.due_from}
              to={filters.due_to}
              onChange={({ from, to }) => updateFilter({ due_from: from, due_to: to })}
            />
          </div>
          <div className="min-w-[260px]">
            <FilterLabel>Team member</FilterLabel>
            <SearchableSelect
              value={filters.assignee_uuid}
              options={[
                { value: '', label: 'All team' },
                ...team.map((m) => ({
                  value: m.uuid,
                  label: `${m.name}${me && m.uuid === me.uuid ? ' (me)' : ''}`,
                  sublabel: m.email
                }))
              ]}
              onChange={(v) => updateFilter({ assignee_uuid: v })}
              placeholder="All team"
              renderIcon={() => <FaUserCircle className="text-gray-400" size={12} />}
              /* Wider popover so name + email read comfortably. */
              popoverClassName="w-[min(420px,calc(100vw-40px))]"
            />
          </div>
          <div className="min-w-[140px]">
            <FilterLabel>Status</FilterLabel>
            <SearchableSelect
              value={filters.status}
              options={[{ value: '', label: 'Any' }, ...STATUS_OPTIONS]}
              onChange={(v) => updateFilter({ status: v })}
              placeholder="Any"
            />
          </div>
          <div className="min-w-[150px]">
            <FilterLabel>Priority</FilterLabel>
            <SearchableSelect
              value={filters.priority}
              options={[
                { value: '', label: 'Any' },
                ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))
              ]}
              onChange={(v) => updateFilter({ priority: v })}
              placeholder="Any"
              renderIcon={(opt) =>
                opt.value ? <PriorityIcon priority={opt.value} size={14} /> : null
              }
              renderRow={(opt, selected) => (
                <>
                  {opt.value ? (
                    <PriorityIcon priority={opt.value} size={14} />
                  ) : (
                    <span style={{ width: 14 }} />
                  )}
                  <span className="flex-1 truncate">{opt.label}</span>
                  {selected ? <span className="text-green-600 text-xs">✓</span> : null}
                </>
              )}
            />
          </div>
          <div className="min-w-[150px]">
            <FilterLabel>Label</FilterLabel>
            <SearchableSelect
              value={filters.label_uuid}
              options={[
                { value: '', label: 'Any' },
                ...labels.map((l) => ({ value: l.uuid, label: l.name, color: l.color }))
              ]}
              onChange={(v) => updateFilter({ label_uuid: v })}
              placeholder="Any"
              renderIcon={(opt) =>
                opt.color ? (
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: opt.color }}
                  />
                ) : null
              }
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                clearFilters();
                setTitleFilter('');
              }}
              title="Clear filters"
              className="reminders-secondary-btn inline-flex items-center gap-1.5 text-xs"
            >
              <FaRedoAlt size={10} /> Reset
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="reminders-primary-btn inline-flex items-center gap-1.5 text-sm font-medium"
            >
              <FaPlus size={11} /> New reminder
            </button>
          </div>
        </div>

        {/* Local title contains-filter — sits between the server-side filter
            card and the table since it only narrows what's already loaded. */}
        <div className="mt-3 flex items-center gap-2">
          <div className="relative w-full max-w-sm">
            <input
              type="text"
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              placeholder="Filter loaded tasks by title…"
              className="h-[34px] w-full rounded-md border border-gray-300 bg-white px-3 py-0 pr-8 text-sm text-gray-800 focus:border-mytPrimary focus:outline-none focus:ring-1 focus:ring-mytPrimary/40"
            />
            {titleFilter ? (
              <button
                type="button"
                onClick={() => setTitleFilter('')}
                aria-label="Clear title filter"
                className="reminders-icon-btn absolute right-1 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center"
              >
                <FaTimes size={10} />
              </button>
            ) : null}
          </div>
          {titleFilter ? (
            <span className="text-xs text-gray-500">
              {sortedTasks.length} of {tasks.length} match
            </span>
          ) : null}
        </div>

        {/* Table */}
        <div className="mt-2 flex min-h-[420px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="grid grid-cols-12 gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
            <div className="col-span-1 text-center">Done</div>
            <div className="col-span-1 flex justify-center">
              <SortHeader label="Priority" sortBy="priority" />
            </div>
            <div className="col-span-4">Task</div>
            <div className="col-span-1">
              <SortHeader label="Due" sortBy="due_at" />
            </div>
            <div className="col-span-2">Labels</div>
            <div className="col-span-1">Assignees</div>
            <div className="col-span-1">User</div>
            <div className="col-span-1">Actions</div>
          </div>

          {sortedTasks.length === 0 && !loading ? (
            <div className="flex flex-1 flex-col items-center justify-center px-3 py-10 text-center text-sm text-gray-400">
              <FaRegBell className="mb-2 text-gray-300" size={22} />
              No reminders match these filters.
            </div>
          ) : (
            sortedTasks.map((task) => {
              const taskAssignees = (task.assignee_uuids || [])
                .map(team_member_by_uuid)
                .filter(Boolean);
              const linked = linked_user_by_uuid(task.linked_user_uuid);
              const taskLabels = (task.label_uuids || []).map(label_by_uuid).filter(Boolean);
              const overdue = isPastDue(task);
              const dueWithinHour = isDueWithinHour(task);
              const dueSoon = isDueSoon(task);
              // Resolve the Due column colour in one place:
              //   completed → light gray (faded — matches the strikethrough title)
              //   past pending → red
              //   ≤1 hour pending → green (drop everything and act)
              //   ≤24 hours pending → blue (it's coming up)
              //   else → default gray
              const isPending = task.status !== 'completed';
              let dueColorCls = 'text-gray-600';
              if (!isPending) dueColorCls = 'text-gray-400';
              else if (overdue) dueColorCls = 'text-red-600 font-medium';
              else if (dueWithinHour) dueColorCls = 'text-green-600 font-medium';
              else if (dueSoon) dueColorCls = 'text-blue-600 font-medium';
              return (
                <div
                  key={task.uuid}
                  className={`grid grid-cols-12 items-start gap-2 border-b border-gray-100 px-3 py-2.5 text-sm transition hover:bg-gray-50 ${
                    overdue ? 'bg-red-50/40' : ''
                  }`}
                >
                  <div className="col-span-1 flex justify-center">
                    <button
                      type="button"
                      onClick={() => handleToggleComplete(task)}
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content={
                        task.status === 'completed' ? 'Mark as pending' : 'Mark as done'
                      }
                      aria-label={task.status === 'completed' ? 'Mark as pending' : 'Mark as done'}
                      className="reminders-icon-btn"
                    >
                      {task.status === 'completed' ? (
                        <FaCheckSquare className="text-green-600" size={18} />
                      ) : (
                        <FaRegSquare size={18} />
                      )}
                    </button>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <PriorityIcon priority={task.priority} size={24} />
                  </div>
                  <div
                    className={`col-span-4 flex min-w-0 items-center gap-1.5 ${
                      task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800'
                    }`}
                    data-tooltip-id="reminders-task-tooltip"
                    data-tooltip-content={task.uuid}
                  >
                    <FaCircleInfo size={12} className="shrink-0 text-gray-400" />
                    <span className="truncate">{task.title}</span>
                  </div>
                  <div
                    className={`col-span-1 inline-flex items-center gap-1 text-xs ${dueColorCls}`}
                  >
                    <FaClock size={10} />
                    <span className="truncate">{formatRelativeDue(task)}</span>
                  </div>
                  <div className="col-span-2 flex flex-col items-start gap-1">
                    {taskLabels.length === 0 ? (
                      <span className="text-[11px] text-gray-400">—</span>
                    ) : (
                      taskLabels.map((l) => (
                        <span
                          key={l.uuid}
                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] text-gray-700"
                          style={{ background: l.color }}
                        >
                          <FaTag size={7} className="opacity-50" />
                          {l.name}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="col-span-1 flex flex-col items-start gap-0.5 text-xs text-gray-600">
                    {taskAssignees.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      taskAssignees.map((a) => {
                        // Compact display: "Rohit Sharma" → "Rohit S".
                        // Keeps full name in the title for hover.
                        const parts = (a.name || '').trim().split(/\s+/);
                        const short =
                          parts.length > 1
                            ? `${parts[0]} ${parts[parts.length - 1][0]}`
                            : parts[0] || '';
                        return (
                          <span
                            key={a.uuid}
                            className="inline-flex items-center gap-1 truncate"
                            title={`${a.name}${a.email ? ` · ${a.email}` : ''}`}
                          >
                            <FaUserCircle size={11} className="text-gray-400" />
                            <span className="truncate">{short}</span>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <div className="col-span-1 flex items-center gap-1 text-xs text-gray-600">
                    {linked ? (
                      (() => {
                        const isTeacher = linked.role === 'teacher';
                        // The CMS uses query-string-based profile routes:
                        //   /studentprofile?id=<uuid> · /teacherprofile?id=<uuid>
                        const href = `${isTeacher ? '/teacherprofile' : '/studentprofile'}?id=${encodeURIComponent(linked.uuid)}`;
                        const firstName = (linked.name || '').split(' ')[0];
                        const roleLabel = isTeacher ? 'Coach' : 'Student';
                        const RoleIcon = isTeacher ? TbCircleLetterT : TbCircleLetterS;
                        // Purple for teachers, MYT theme orange for students —
                        // applied to both the icon and the first-name text.
                        const roleColor = isTeacher ? '#9333ea' : '#ee731b';
                        return (
                          <Link
                            to={href}
                            className="inline-flex items-center gap-1 truncate hover:underline"
                            style={{ color: roleColor }}
                            title={`Open ${roleLabel.toLowerCase()} profile — ${linked.name}`}
                          >
                            <RoleIcon size={18} />
                            <span className="truncate">{firstName}</span>
                          </Link>
                        );
                      })()
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setViewingTask(task)}
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content="View details & notes"
                      aria-label="View reminder details"
                      className="reminders-icon-btn"
                    >
                      <FaRegNoteSticky size={15} />
                    </button>
                    <RowActionsMenu
                      items={[
                        {
                          label: 'Edit',
                          icon: FaRegEdit,
                          onClick: () => handleStartEdit(task)
                        },
                        {
                          label: 'Clone',
                          icon: FaRegClone,
                          onClick: () => handleClone(task)
                        },
                        {
                          label: 'Delete',
                          icon: FaRegTrashAlt,
                          destructive: true,
                          onClick: () => handleDelete(task)
                        }
                      ]}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-2 text-[11px] text-gray-400">
          Showing {tasks.length} reminder{tasks.length === 1 ? '' : 's'}
        </div>

        {/* Create / edit / clone modal */}
        <CustomModal
          show={showCreate}
          close={handleCloseModal}
          title={editingTask ? 'Edit reminder' : cloneSeed ? 'Clone reminder' : 'New reminder'}
          inner_style={{ width: 'min(640px, 96vw)', maxWidth: 'none' }}
        >
          <ReminderForm
            // Remount when the seed changes so internal form state resets.
            key={editingTask?.uuid || (cloneSeed ? 'clone' : 'new')}
            initial={taskToFormInitial(editingTask || cloneSeed)}
            defaultAssigneeUuid={me?.uuid}
            selfUuid={me?.uuid}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
          />
        </CustomModal>

        {/* View-details modal (read-only metadata + notes thread) */}
        <ReminderDetailsModal
          show={!!viewingTask}
          close={() => setViewingTask(null)}
          task={viewingTask}
          onUpdated={(updated) => {
            // Reflect new note locally and in the list view.
            setViewingTask(updated);
            refresh();
          }}
        />

        {/* In-app delete confirmation, replaces the prior window.confirm call. */}
        <CustomModal show={!!taskPendingDelete} close={() => setTaskPendingDelete(null)}>
          <ConfirmationPopup
            title={
              taskPendingDelete ? `Delete "${taskPendingDelete.title}"? This can't be undone.` : ''
            }
            close_modal={() => setTaskPendingDelete(null)}
            confirm={confirmDelete}
          />
        </CustomModal>
      </div>
    </CommonHeader>
  );
}

export default Reminders;
